/**
 * Module-level pipeline manager.
 *
 * Runs intake and flow pipelines outside React's component lifecycle so
 * state survives component unmount/remount (e.g. patient tab switches).
 *
 * Ported from JSX (master:frontend/src/services/pipelineManager.js)
 * to TypeScript for the Python/FastAPI backend.
 *
 * Intake pipeline: 5 agents (hpi-pe-agent, 3 casey slots, clinical-reasoning)
 * Flow pipeline: sequential IPASS steps via Casey-only
 */

import { backendAdapter } from './backendAdapter'
import type { CasePanelSSEEvent } from './backendAdapter'
import { workspaceService } from './workspaceService'
import { generateId } from '../utils/idUtils'
import { getEventType } from '../utils/sseUtils'

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

export type PipelineStatus = 'idle' | 'running' | 'complete' | 'error'
export type AgentPipelineStatus = 'waiting' | 'running' | 'data-ready' | 'complete' | 'error'

export const PIPELINE_AGENT_IDS = ['hpi-pe-agent', 'casey-extract', 'casey-data', 'casey-clinical', 'clinical-reasoning'] as const
export type FlowStepStatus = 'waiting' | 'running' | 'complete' | 'error'

export interface PipelineEvent {
  id: string
  agentId: string
  type: string
  description: string
  timestamp: number
}

export interface PipelineResult {
  output: string
  tools: Array<{ name: string; input: string; output: string }>
}

export interface CaseyPreviewData {
  patientSummary: string
  /** @deprecated Legacy blob format — replaced by structured presentation/admissionContext fields. Remove once old workspaces are migrated. */
  panelContent: string | null
  presentation: string | null
  admissionContext: string | null
}

export interface PipelineSnapshot {
  status: PipelineStatus
  agentStatus: Record<string, AgentPipelineStatus>
  results: Record<string, PipelineResult>
  caseyPreview: CaseyPreviewData | null
  events: PipelineEvent[]
  error: string | null
  startTime: number
  agentStartTimes: Record<string, number>
  agentEndTimes: Record<string, number>
  fileCount: number
}

interface FlowStepConfig {
  id: string
  label: string
  message: string
  needsFiles?: boolean
}

// Fix #238: Snapshot exposes only data, not resolve callbacks from QueuedMessage
export interface FlowPipelineSnapshot {
  status: PipelineStatus
  steps: string[]
  currentStep: number
  stepStatus: Record<string, FlowStepStatus>
  stepResults: Record<string, PipelineResult>
  sessionId: string
  events: PipelineEvent[]
  error: string | null
  startTime: number
  stepStartTimes: Record<string, number>
  stepEndTimes: Record<string, number>
  fileCount: number
  paused: boolean
  userMessageQueue: Array<{ text: string; files: File[] }>
}

interface QueuedMessage {
  text: string
  files: File[]
  resolve: (result: PipelineResult | null) => void
}

type PipelineSubscriber = (snapshot: PipelineSnapshot) => void
type FlowPipelineSubscriber = (snapshot: FlowPipelineSnapshot) => void

interface IntakePipelineState {
  status: PipelineStatus
  agentStatus: Record<string, AgentPipelineStatus>
  results: Record<string, PipelineResult>
  caseyPreview: CaseyPreviewData | null
  events: PipelineEvent[]
  subscribers: Set<PipelineSubscriber>
  error: string | null
  startTime: number
  agentStartTimes: Record<string, number>
  agentEndTimes: Record<string, number>
  fileCount: number
  ipassContext: string
  caseySessionId?: string
  hpiPeSessionId?: string
  crSessionId?: string
}

interface FlowPipelineState {
  status: PipelineStatus
  steps: string[]
  currentStep: number
  stepStatus: Record<string, FlowStepStatus>
  stepResults: Record<string, PipelineResult>
  sessionId: string
  events: PipelineEvent[]
  subscribers: Set<FlowPipelineSubscriber>
  error: string | null
  startTime: number
  stepStartTimes: Record<string, number>
  stepEndTimes: Record<string, number>
  fileCount: number
  paused: boolean
  userMessageQueue: QueuedMessage[]
  files: File[] | null
}

// ────────────────────────────────────────────────────────
// Module state (survives React lifecycle)
// ────────────────────────────────────────────────────────

const pipelines = new Map<string, IntakePipelineState>()
const flowPipelines = new Map<string, FlowPipelineState>()
const abortControllers = new Map<string, AbortController>()

// ────────────────────────────────────────────────────────
// Internal helpers
// ────────────────────────────────────────────────────────

const generateSessionId = (): string =>
  crypto.randomUUID()

const notify = (patientId: string): void => {
  const p = pipelines.get(patientId)
  if (!p) return
  const snap = buildSnapshot(p)
  p.subscribers.forEach(cb => cb(snap))
}

const buildSnapshot = (p: IntakePipelineState): PipelineSnapshot => ({
  status: p.status,
  agentStatus: { ...p.agentStatus },
  results: { ...p.results },
  caseyPreview: p.caseyPreview,
  events: [...p.events],
  error: p.error,
  startTime: p.startTime,
  agentStartTimes: { ...p.agentStartTimes },
  agentEndTimes: { ...p.agentEndTimes },
  fileCount: p.fileCount,
})

const buildResult = (output: string, tools: Array<{ name: string; input: string; output: string }> = []): PipelineResult => ({
  output,
  tools,
})

// ────────────────────────────────────────────────────────
// Intake Pipeline — Public API
// ────────────────────────────────────────────────────────

export const getSnapshot = (patientId: string): PipelineSnapshot | null => {
  const p = pipelines.get(patientId)
  return p ? buildSnapshot(p) : null
}

export const hasPipeline = (patientId: string): boolean =>
  pipelines.has(patientId)

export const subscribe = (patientId: string, callback: PipelineSubscriber): (() => void) => {
  const p = pipelines.get(patientId)
  if (!p) return () => {}
  p.subscribers.add(callback)
  return () => p.subscribers.delete(callback)
}

export const clearPipeline = (patientId: string): void => {
  const controller = abortControllers.get(`intake-${patientId}`)
  if (controller) {
    controller.abort()
    abortControllers.delete(`intake-${patientId}`)
  }
  pipelines.delete(patientId)
}

export const startPipeline = (patientId: string, workspaceId: string): void => {
  if (pipelines.has(patientId)) return

  // Check for IPASS context from a flow->admission conversion
  let ipassContext = ''
  try {
    const stored = sessionStorage.getItem(`casey-ipass-context-${patientId}`)
    if (stored) {
      ipassContext = stored
      sessionStorage.removeItem(`casey-ipass-context-${patientId}`)
    }
  } catch (err) { console.error('[PipelineManager] Failed to read IPASS context from sessionStorage:', err) }

  const state: IntakePipelineState = {
    status: 'running',
    agentStatus: Object.fromEntries(PIPELINE_AGENT_IDS.map(id => [id, 'waiting' as AgentPipelineStatus])),
    results: {},
    caseyPreview: null,
    events: [],
    subscribers: new Set(),
    error: null,
    startTime: Date.now(),
    agentStartTimes: {},
    agentEndTimes: {},
    fileCount: 0,
    ipassContext,
  }
  pipelines.set(patientId, state)
  executePipeline(patientId, workspaceId, state)
}

// ────────────────────────────────────────────────────────
// Intake Pipeline — Execution
// ────────────────────────────────────────────────────────

/**
 * Execute the 3-agent intake pipeline.
 *
 * Adapted for Python backend: uses /api/v1/case-panel/chat endpoint
 * with SSE streaming per agent. Each agent runs in its own session.
 */
const executePipeline = async (
  patientId: string,
  workspaceId: string,
  state: IntakePipelineState,
): Promise<void> => {
  const addEvent = (agentId: string, type: string, description: string): void => {
    state.events.push({
      id: generateId(),
      agentId,
      type,
      description,
      timestamp: Date.now(),
    })
    notify(patientId)
  }

  const setAgentStatus = (agentId: string, status: AgentPipelineStatus): void => {
    state.agentStatus[agentId] = status
    if (status === 'running') state.agentStartTimes[agentId] = Date.now()
    if (status === 'complete' || status === 'error') state.agentEndTimes[agentId] = Date.now()
    notify(patientId)
  }

  const ipassPreamble = state.ipassContext
    ? `\n\nThe patient was previously evaluated as a flow case. Here is the AI-generated IPASS signout from that evaluation:\n\n--- IPASS SIGNOUT ---\n${state.ipassContext}\n--- END IPASS SIGNOUT ---\n\nUse this IPASS context alongside the chart data to generate a complete admission workup.`
    : ''

  let settledCount = 0
  const totalAgents = 5

  const checkSettled = (): void => {
    settledCount++
    if (settledCount === totalAgents) {
      state.status = state.error ? 'error' : 'complete'
      // Clean up abort controller on natural completion
      abortControllers.delete(`intake-${patientId}`)
      notify(patientId)
    }
  }

  /**
   * Stream a message to an agent via SSE and collect the full response.
   */
  const streamAgentMessage = async (
    agentId: string,
    message: string,
    sessionId?: string,
    onToolComplete?: (toolName: string) => void,
  ): Promise<PipelineResult> => {
    const resolvedSessionId = sessionId || generateSessionId()
    let output = ''
    const tools: Array<{ name: string; input: string; output: string }> = []

    // Create per-pipeline AbortController for cleanup
    const controllerKey = `intake-${patientId}`
    if (!abortControllers.has(controllerKey)) {
      abortControllers.set(controllerKey, new AbortController())
    }
    const signal = abortControllers.get(controllerKey)!.signal

    const stream = backendAdapter.streamCasePanelChat({
      workspaceId,
      agentId,
      message,
      mode: 'intake',
      // Composed here, not typed by anyone (mode:'intake' already keeps the
      // manual-edit notice off this turn; the flag is set for honesty, so a
      // reader never has to ask which dispatches are automated).
      source: 'pipeline',
      sessionId: resolvedSessionId,
      signal,
    })

    for await (const event of stream) {
      const sseEvent = event as CasePanelSSEEvent
      const eventType = getEventType(sseEvent)
      const data = sseEvent.data || {}

      switch (eventType) {
        case 'message':
        case 'token':
          if (data.content) output += data.content
          break
        case 'tool_start':
          addEvent(agentId, 'tool_call', `Running ${data.tool_name || data.tool || 'tool'}...`)
          break
        case 'tool_end': {
          const toolName = data.tool_name || data.tool || 'unknown'
          addEvent(agentId, 'tool_result', `${toolName} complete`)
          tools.push({
            name: toolName,
            input: typeof data.input === 'string' ? data.input : JSON.stringify(data.input || ''),
            output: typeof data.output === 'string' ? data.output : JSON.stringify(data.output || ''),
          })
          onToolComplete?.(toolName)
          break
        }
        case 'complete':
          if (data.content) output = data.content
          break
        case 'error': {
          const msg = (data.message || data.error || 'Agent error') as string
          const err = new Error(msg) as Error & { errorCode?: string; retryable?: boolean }
          const code = (data.error_code || data.code) as string | undefined
          if (code) err.errorCode = code
          if (typeof data.retryable === 'boolean') err.retryable = data.retryable
          throw err
        }
      }
    }

    return buildResult(output, tools)
  }

  // The two causes behind chart_data_unavailable need opposite user actions:
  // a chart that never arrived needs re-uploading, a chart the backend failed to
  // read is already there and just needs the retry. Telling a physician to
  // re-upload a chart that uploaded fine sends them chasing the wrong problem.
  const chartUnavailableMessage = (agentLabel: string, err?: unknown): string => {
    const recovery = (err as { retryable?: boolean } | undefined)?.retryable
      ? 'Loading it failed — the chart is already uploaded, so retry.'
      : 'Re-upload screenshots or wait for OCR to finish, then retry.'
    return `${agentLabel} aborted: chart data could not be loaded. ${recovery}`
  }

  const isChartDataUnavailable = (err: unknown): boolean =>
    err instanceof Error && (err as Error & { errorCode?: string }).errorCode === 'chart_data_unavailable'

  // Chart text reaches agents server-side: slot agents get a developer-message
  // pre-inject keyed on agent_id (hard-abort if chart data never lands); the
  // historian pre-load keys on the 'INTAKE:' message prefix. The user message
  // only steers — it never carries the chart.
  const slotChartNote = '\n\nChart data has been saved to the database — call load_chart_data to access it.'
  const historianChartNote = '\n\nChart data has been saved to the workspace and will be pre-loaded for you; if it is missing, use extract_chunk or search_chart to read it.'

  // Helper: hydrate caseyPreview on each casey slot completion
  const hydratePreview = async () => {
    try {
      const hydration = await workspaceService.hydrate(workspaceId)
      const cd = hydration.clinical_data
      const summary = cd?.patient_summary || cd?.panel_content
      state.caseyPreview = summary ? {
        patientSummary: cd!.patient_summary || '',
        panelContent: cd!.panel_content || null,
        presentation: cd!.summary_presentation || null,
        admissionContext: cd!.summary_admission_context || null,
      } : null
      notify(patientId)
    } catch (err) {
      console.error('[PipelineManager] Failed to hydrate workspace for patient preview:', err)
    }
  }

  // Casey Slot 1: Semantic Extractions (gpt-5.4-mini)
  const caseyExtractPromise = (async () => {
    addEvent('casey-extract', 'started', 'Extracting clinical items')
    setAgentStatus('casey-extract', 'running')
    try {
      const session = generateSessionId()
      const message = `Extract all clinical items from this patient's chart data. Call save_extractions with every problem, medication, lab, vital, imaging finding, procedure, and plan.${ipassPreamble}${slotChartNote}`
      const result = await streamAgentMessage('casey-intake-extract', message, session)
      state.results.caseyExtract = result
      await hydratePreview()
      addEvent('casey-extract', 'complete', 'Extractions saved')
      setAgentStatus('casey-extract', 'complete')
      return result
    } catch (err: unknown) {
      const displayMsg = isChartDataUnavailable(err)
        ? chartUnavailableMessage('Casey Extract', err)
        : `Error: ${err instanceof Error ? err.message : String(err)}`
      addEvent('casey-extract', 'error', displayMsg)
      setAgentStatus('casey-extract', 'error')
      throw err
    }
  })()

  // Casey Slot 2: Structured Data (gpt-5.4-mini)
  const caseyDataPromise = (async () => {
    addEvent('casey-data', 'started', 'Structuring patient summary, vitals, labs & imaging')
    setAgentStatus('casey-data', 'running')
    try {
      const session = generateSessionId()
      const message = `Extract structured clinical data from this patient's chart. Call save_clinical_data with summary, vitals, labs, imaging, and demographics sections.${ipassPreamble}${slotChartNote}`
      const result = await streamAgentMessage('casey-intake-data', message, session)
      state.results.caseyData = result
      await hydratePreview()
      addEvent('casey-data', 'complete', 'Patient data structured')
      setAgentStatus('casey-data', 'complete')
      return result
    } catch (err: unknown) {
      const displayMsg = isChartDataUnavailable(err)
        ? chartUnavailableMessage('Casey Data', err)
        : `Error: ${err instanceof Error ? err.message : String(err)}`
      addEvent('casey-data', 'error', displayMsg)
      setAgentStatus('casey-data', 'error')
      throw err
    }
  })()

  // Casey Slot 3: Clinical Reasoning (gpt-5.2)
  const caseyClinicalPromise = (async () => {
    addEvent('casey-clinical', 'started', 'Clinical reasoning — orders & meds')
    setAgentStatus('casey-clinical', 'running')
    try {
      const session = generateSessionId()
      state.caseySessionId = session
      const message = `Analyze this patient's chart data for admission. Call save_clinical_data with orders and medications sections. Then call reconcile_meds.${ipassPreamble}${slotChartNote}`
      let clinicalDataSaved = false
      const result = await streamAgentMessage('casey-intake-clinical', message, session, (toolName) => {
        if (toolName === 'save_clinical_data' && !clinicalDataSaved) {
          clinicalDataSaved = true
          addEvent('casey-clinical', 'info', 'Orders & meds saved — reconciling...')
          setAgentStatus('casey-clinical', 'data-ready')
        }
      })
      state.results.caseyClinical = result
      await hydratePreview()
      addEvent('casey-clinical', 'complete', 'Clinical reasoning complete')
      setAgentStatus('casey-clinical', 'complete')
      return result
    } catch (err: unknown) {
      const displayMsg = isChartDataUnavailable(err)
        ? chartUnavailableMessage('Casey Clinical', err)
        : `Error: ${err instanceof Error ? err.message : String(err)}`
      addEvent('casey-clinical', 'error', displayMsg)
      setAgentStatus('casey-clinical', 'error')
      throw err
    }
  })()

  // HPI-PE Agent (The Historian)
  const hpiPromise = (async () => {
    addEvent('hpi-pe-agent', 'started', state.ipassContext
      ? 'Generating HPI & PE from chart data (with IPASS signout)'
      : 'Generating HPI & PE from chart data')
    setAgentStatus('hpi-pe-agent', 'running')

    try {
      const hpiSession = generateSessionId()
      state.hpiPeSessionId = hpiSession
      const hpiMessage = `INTAKE: Generate a complete HPI and Physical Exam. Follow your 4-step intake workflow.${ipassPreamble}${historianChartNote}`
      const result = await streamAgentMessage('hpi-pe-agent', hpiMessage, hpiSession)
      state.results.hpiPe = result
      addEvent('hpi-pe-agent', 'complete', 'The Historian draft generated')
      setAgentStatus('hpi-pe-agent', 'complete')
      return result
    } catch (err: unknown) {
      const displayMsg = isChartDataUnavailable(err)
        ? chartUnavailableMessage('The Historian', err)
        : `Error: ${err instanceof Error ? err.message : String(err)}`
      addEvent('hpi-pe-agent', 'error', displayMsg)
      setAgentStatus('hpi-pe-agent', 'error')
      throw err
    }
  })()

  // Clinical Reasoning (A&P) — waits for HPI-PE to finish
  const crPromise = (async () => {
    let hpiSucceeded = false
    try {
      await hpiPromise
      hpiSucceeded = true
    } catch (err) {
      console.error('[PipelineManager] HPI-PE agent failed (awaited by clinical-reasoning):', err)
      // Every other historian failure the A&P can work around — its prompt
      // already tolerates a partial load_hpi_pe. A missing chart it cannot: the
      // A&P reads the same chart, so running it drafts and auto-saves an
      // assessment from nothing, seconds after the UI reported the abort (#473).
      if (isChartDataUnavailable(err)) {
        addEvent('clinical-reasoning', 'error', chartUnavailableMessage('Clinical Reasoning', err))
        setAgentStatus('clinical-reasoning', 'error')
        throw err
      }
    }
    await new Promise(r => setTimeout(r, 1000))

    addEvent('clinical-reasoning', 'started', 'Generating initial Assessment & Plan')
    setAgentStatus('clinical-reasoning', 'running')

    try {
      const crSession = generateSessionId()
      state.crSessionId = crSession
      const apMessage = hpiSucceeded
        ? 'The Historian has finished generating the HPI+PE and saved it. Follow your First Message Protocol:\n1. Call load_hpi_pe to load the HPI+PE draft.\n2. Call load_chart_data to access the raw chart data.\n3. Call load_structured_chart to access the structured/organized chart data (if available — if not_found, proceed with the raw chart data from step 2).\n4. Call research with a focused clinical query derived from the main diagnoses.\n5. Call generate_ap with the FULL HPI+PE text as CASE_TEXT.\n6. Call determine_billing with the case summary and generated A&P.\nPresent the completed A&P draft when done. Do NOT fail or stop if any single data source is unavailable — use what you have.'
        : 'The Historian encountered an error and may not have saved HPI+PE. Try calling load_hpi_pe anyway (it may have partial data). Follow your First Message Protocol:\n1. Call load_hpi_pe (may have partial data).\n2. Call load_chart_data to access the raw chart data.\n3. Call load_structured_chart to access the structured/organized chart data (if available — if not_found, proceed with the raw chart data from step 2).\n4. Call research with a focused clinical query derived from the main diagnoses.\n5. Call generate_ap with the FULL HPI+PE text as CASE_TEXT.\n6. Call determine_billing with the case summary and generated A&P.\nPresent the completed A&P draft when done. Do NOT fail or stop if any single data source is unavailable — use what you have.'
      const result = await streamAgentMessage('clinical-reasoning', apMessage, crSession)
      state.results.clinicalReasoning = result
      addEvent('clinical-reasoning', 'complete', 'A&P draft generated')
      setAgentStatus('clinical-reasoning', 'complete')
      return result
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      addEvent('clinical-reasoning', 'error', `Error: ${message}`)
      setAgentStatus('clinical-reasoning', 'error')
      throw err
    }
  })()

  // Settlement tracking
  const settleAgent = (label: string, err: Error): void => {
    const msg = isChartDataUnavailable(err)
      ? chartUnavailableMessage(label, err)
      : `${label} failed: ${err.message}`
    state.error = state.error ? `${state.error}; ${msg}` : msg
    notify(patientId)
    checkSettled()
  }

  hpiPromise
    .then(() => checkSettled())
    .catch((err: Error) => settleAgent('The Historian', err))

  caseyExtractPromise
    .then(() => checkSettled())
    .catch((err: Error) => settleAgent('Casey Extract', err))

  caseyDataPromise
    .then(() => {
      addEvent('casey-data', 'info', 'Patient panel data ready')
      checkSettled()
    })
    .catch((err: Error) => settleAgent('Casey Data', err))

  caseyClinicalPromise
    .then(() => {
      addEvent('casey-clinical', 'info', 'Clinical reasoning ready')
      checkSettled()
    })
    .catch((err: Error) => settleAgent('Casey Clinical', err))

  crPromise
    .then(() => {
      addEvent('clinical-reasoning', 'info', 'A&P ready')
      checkSettled()
    })
    .catch((err: Error) => settleAgent('Clinical Reasoning', err))
}

// ════════════════════════════════════════════════════════
// Flow Pipeline — Sequential IPASS step processing
// ════════════════════════════════════════════════════════

const FLOW_STEPS: FlowStepConfig[] = [
  {
    id: 'parse',
    label: 'Parsing Clinical Text',
    message: '[FLOW_STEP:parse] Process the attached chart data. Extract all relevant clinical data: chief complaint, HPI, diagnoses, medications, labs, vitals, imaging. Be CONCISE. Write the I, P, and VD sections using <!-- IPASS:I -->, <!-- IPASS:P -->, and <!-- IPASS:VD --> markers.',
    needsFiles: true,
  },
  {
    id: 'clinical',
    label: 'Analyzing Clinical Picture',
    message: '[FLOW_STEP:clinical] Analyze the clinical picture. Generate the A and S1 sections using <!-- IPASS:A --> and <!-- IPASS:S1 --> markers.',
  },
  {
    id: 'loc',
    label: 'Determining Level of Care',
    message: '[FLOW_STEP:loc] Determine final disposition using the structured clinical decision algorithm. Write the S2 section using <!-- IPASS:S2 --> marker. Output the structured LOC comment: <!-- LOC: {...} -->',
  },
]

const flowNotify = (patientId: string): void => {
  const p = flowPipelines.get(patientId)
  if (!p) return
  const snap = buildFlowSnapshot(p)
  p.subscribers.forEach(cb => cb(snap))
}

const buildFlowSnapshot = (p: FlowPipelineState): FlowPipelineSnapshot => ({
  status: p.status,
  steps: [...p.steps],
  currentStep: p.currentStep,
  stepStatus: { ...p.stepStatus },
  stepResults: { ...p.stepResults },
  sessionId: p.sessionId,
  events: [...p.events],
  error: p.error,
  startTime: p.startTime,
  stepStartTimes: { ...p.stepStartTimes },
  stepEndTimes: { ...p.stepEndTimes },
  fileCount: p.fileCount,
  paused: p.paused,
  // Fix #238: Strip resolve callbacks — snapshots should be pure data
  userMessageQueue: p.userMessageQueue.map(q => ({ text: q.text, files: q.files })),
})

export const getFlowSnapshot = (patientId: string): FlowPipelineSnapshot | null => {
  const p = flowPipelines.get(patientId)
  return p ? buildFlowSnapshot(p) : null
}

export const hasFlowPipeline = (patientId: string): boolean =>
  flowPipelines.has(patientId)

export const subscribeFlow = (patientId: string, callback: FlowPipelineSubscriber): (() => void) => {
  const p = flowPipelines.get(patientId)
  if (!p) return () => {}
  p.subscribers.add(callback)
  return () => p.subscribers.delete(callback)
}

export const clearFlowPipeline = (patientId: string): void => {
  const controller = abortControllers.get(`flow-${patientId}`)
  if (controller) {
    controller.abort()
    abortControllers.delete(`flow-${patientId}`)
  }
  const p = flowPipelines.get(patientId)
  if (p && p.userMessageQueue) {
    // Resolve pending user message promises to prevent hanging awaits
    p.userMessageQueue.forEach(q => q.resolve(null))
    p.userMessageQueue.length = 0
  }
  flowPipelines.delete(patientId)
  try { sessionStorage.removeItem(`casey-flow-pipeline-${patientId}`) } catch (err) { console.warn('[PipelineManager] sessionStorage removeItem failed for flow pipeline:', err) }
  try { sessionStorage.removeItem(`casey-flow-chat-${patientId}`) } catch (err) { console.warn('[PipelineManager] sessionStorage removeItem failed for flow chat:', err) }
}

export const getFlowSessionId = (patientId: string): string | null => {
  const p = flowPipelines.get(patientId)
  return p?.sessionId || null
}

/**
 * Persist flow pipeline state to sessionStorage for page reload survival.
 */
const persistFlowState = (patientId: string): void => {
  const p = flowPipelines.get(patientId)
  if (!p) return
  try {
    const serializable = {
      status: p.status,
      steps: p.steps,
      currentStep: p.currentStep,
      stepStatus: p.stepStatus,
      stepResults: p.stepResults,
      sessionId: p.sessionId,
      events: p.events,
      error: p.error,
      startTime: p.startTime,
      stepStartTimes: p.stepStartTimes,
      stepEndTimes: p.stepEndTimes,
      fileCount: p.fileCount,
    }
    sessionStorage.setItem(`casey-flow-pipeline-${patientId}`, JSON.stringify(serializable))
  } catch (err) { console.warn('[PipelineManager] sessionStorage quota exceeded persisting flow state:', err) }
}

/**
 * Restore flow pipeline state from sessionStorage.
 */
export const restoreFlowState = (patientId: string): Partial<FlowPipelineState> | null => {
  try {
    const raw = sessionStorage.getItem(`casey-flow-pipeline-${patientId}`)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (err) { console.error('[PipelineManager] Failed to restore flow state from sessionStorage:', err); return null }
}

/**
 * Resume a flow pipeline from persisted state.
 */
export const resumeFlowPipeline = (patientId: string, workspaceId: string): void => {
  if (flowPipelines.has(patientId)) return

  const saved = restoreFlowState(patientId)
  if (!saved) return

  const state: FlowPipelineState = {
    status: saved.status || 'idle',
    steps: saved.steps || FLOW_STEPS.map(s => s.id),
    currentStep: saved.currentStep || 0,
    stepStatus: { ...(saved.stepStatus || {}) },
    stepResults: { ...(saved.stepResults || {}) },
    sessionId: saved.sessionId || generateSessionId(),
    events: [...(saved.events || [])],
    subscribers: new Set(),
    error: saved.error || null,
    startTime: saved.startTime || Date.now(),
    stepStartTimes: { ...(saved.stepStartTimes || {}) },
    stepEndTimes: { ...(saved.stepEndTimes || {}) },
    fileCount: saved.fileCount || 0,
    paused: false,
    userMessageQueue: [],
    files: null,
  }
  flowPipelines.set(patientId, state)

  if (saved.status === 'complete' || saved.status === 'error') {
    flowNotify(patientId)
    return
  }

  const resumeFrom = state.steps.findIndex(stepId => state.stepStatus[stepId] !== 'complete')
  if (resumeFrom < 0) {
    state.status = 'complete'
    flowNotify(patientId)
    return
  }

  state.currentStep = resumeFrom
  state.status = 'running'
  state.stepStatus[state.steps[resumeFrom]] = 'waiting'
  state.events.push({
    id: `${Date.now()}-resume`,
    agentId: 'casey',
    type: 'info',
    description: `Resuming from step ${resumeFrom + 1}: ${FLOW_STEPS[resumeFrom]?.label || 'unknown'}`,
    timestamp: Date.now(),
  })
  flowNotify(patientId)

  executeFlowPipeline(patientId, workspaceId, state, resumeFrom)
}

export const startFlowPipeline = (
  patientId: string,
  workspaceId: string,
  sessionId?: string,
): void => {
  if (flowPipelines.has(patientId)) return

  const state: FlowPipelineState = {
    status: 'running',
    steps: FLOW_STEPS.map(s => s.id),
    currentStep: 0,
    stepStatus: {},
    stepResults: {},
    sessionId: sessionId || generateSessionId(),
    events: [],
    subscribers: new Set(),
    error: null,
    startTime: Date.now(),
    stepStartTimes: {},
    stepEndTimes: {},
    fileCount: 0,
    paused: false,
    userMessageQueue: [],
    files: null,
  }
  FLOW_STEPS.forEach(s => { state.stepStatus[s.id] = 'waiting' })
  flowPipelines.set(patientId, state)
  executeFlowPipeline(patientId, workspaceId, state, 0)
}

/**
 * Send a user message during flow processing.
 * If a step is running, queues it. Otherwise sends immediately.
 */
export const sendFlowUserMessage = async (
  patientId: string,
  workspaceId: string,
  text: string,
): Promise<PipelineResult | null> => {
  const p = flowPipelines.get(patientId)
  if (!p) return null

  const addEvent = (type: string, description: string): void => {
    p.events.push({
      id: generateId(),
      agentId: 'user',
      type,
      description,
      timestamp: Date.now(),
    })
    flowNotify(patientId)
  }

  addEvent('user_message', text)

  // If a step is currently running, queue the message
  const currentStepId = FLOW_STEPS[p.currentStep]?.id
  if (currentStepId && p.stepStatus[currentStepId] === 'running') {
    return new Promise(resolve => {
      p.userMessageQueue.push({ text, files: [], resolve })
    })
  }

  // Otherwise send immediately via SSE
  try {
    let output = ''
    const tools: Array<{ name: string; input: string; output: string }> = []

    const controllerKey = `flow-${patientId}`
    const flowSignal = abortControllers.get(controllerKey)?.signal

    const stream = backendAdapter.streamCasePanelChat({
      workspaceId,
      agentId: 'casey',
      message: text,
      mode: 'flow',
      sessionId: p.sessionId,
      signal: flowSignal,
    })

    for await (const event of stream) {
      const sseEvent = event as CasePanelSSEEvent
      const eventType = getEventType(sseEvent)
      const data = sseEvent.data || {}

      if (eventType === 'message' || eventType === 'token') {
        if (data.content) output += data.content
      } else if (eventType === 'tool_end') {
        tools.push({
          name: data.tool_name || data.tool || 'unknown',
          input: typeof data.input === 'string' ? data.input : JSON.stringify(data.input || ''),
          output: typeof data.output === 'string' ? data.output : JSON.stringify(data.output || ''),
        })
      } else if (eventType === 'complete' && data.content) {
        output = data.content
      }
    }

    const result = buildResult(output, tools)
    addEvent('casey_response', `output length: ${result.output?.length || 0}`)
    return result
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err)
    addEvent('error', `Message failed: ${message}`)
    return null
  }
}

/**
 * Execute the flow pipeline step by step via SSE.
 */
const executeFlowPipeline = async (
  patientId: string,
  workspaceId: string,
  state: FlowPipelineState,
  startFrom = 0,
): Promise<void> => {
  const addEvent = (agentId: string, type: string, description: string): void => {
    state.events.push({
      id: generateId(),
      agentId,
      type,
      description,
      timestamp: Date.now(),
    })
    flowNotify(patientId)
  }

  const STEP_MAX_RETRIES = 1

  // Create per-pipeline AbortController for cleanup
  const controllerKey = `flow-${patientId}`
  if (!abortControllers.has(controllerKey)) {
    abortControllers.set(controllerKey, new AbortController())
  }
  const signal = abortControllers.get(controllerKey)!.signal

  for (let i = startFrom; i < FLOW_STEPS.length; i++) {
    const step = FLOW_STEPS[i]
    state.currentStep = i
    state.stepStatus[step.id] = 'running'
    state.stepStartTimes[step.id] = Date.now()
    addEvent('casey', 'step_started', `Step ${i + 1}: ${step.label}`)
    flowNotify(patientId)

    let lastError: Error | null = null
    let output = ''
    const tools: Array<{ name: string; input: string; output: string }> = []

    for (let attempt = 0; attempt <= STEP_MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          addEvent('casey', 'info', `Retrying step ${i + 1} (attempt ${attempt + 1})...`)
          await new Promise(r => setTimeout(r, 2000))
        }

        output = ''
        tools.length = 0

        // For the first step (needsFiles), instruct agent to load chart data from DB
        let stepMessage = step.message
        if (step.needsFiles) {
          stepMessage += '\n\nChart data has been saved to the database — call load_chart_data to access it.'
        }

        const stream = backendAdapter.streamCasePanelChat({
          workspaceId,
          agentId: 'casey',
          message: stepMessage,
          mode: 'flow',
          // The pipeline composed this step; no physician is waiting on it.
          // Without the flag the backend cannot tell it apart from a typed turn
          // and spends the one-shot manual-edit notice on it. The other two
          // mode:'flow' dispatches in this file (sendFlowUserMessage's immediate
          // send and the queued-message drain) deliberately do NOT set it: those
          // carry the physician's own typed text.
          source: 'pipeline',
          sessionId: state.sessionId,
          signal,
        })

        for await (const event of stream) {
          const sseEvent = event as CasePanelSSEEvent
          const eventType = getEventType(sseEvent)
          const data = sseEvent.data || {}

          if (eventType === 'message' || eventType === 'token') {
            if (data.content) output += data.content
          } else if (eventType === 'tool_start') {
            addEvent('casey', 'tool_call', `Running ${data.tool_name || data.tool || 'tool'}...`)
          } else if (eventType === 'tool_end') {
            addEvent('casey', 'tool_result', `${data.tool_name || data.tool || 'tool'} complete`)
            tools.push({
              name: data.tool_name || data.tool || 'unknown',
              input: typeof data.input === 'string' ? data.input : JSON.stringify(data.input || ''),
              output: typeof data.output === 'string' ? data.output : JSON.stringify(data.output || ''),
            })
          } else if (eventType === 'complete' && data.content) {
            output = data.content
          } else if (eventType === 'error') {
            throw new Error((data.message || data.error || 'Step error') as string)
          }
        }

        lastError = null
        break
      } catch (err: unknown) {
        lastError = err instanceof Error ? err : new Error(String(err))
        console.warn(`[FlowPipeline] Step "${step.id}" attempt ${attempt + 1} failed:`, lastError.message)
      }
    }

    if (lastError) {
      state.stepStatus[step.id] = 'error'
      state.stepEndTimes[step.id] = Date.now()
      state.error = `${step.label} failed: ${lastError.message}`
      addEvent('casey', 'error', state.error)
      state.status = 'error'
      // Clean up abort controller on error exit
      abortControllers.delete(`flow-${patientId}`)
      // Drain queued user messages to prevent hanging promises
      while (state.userMessageQueue.length > 0) {
        state.userMessageQueue.shift()!.resolve(null)
      }
      flowNotify(patientId)
      persistFlowState(patientId)
      return
    }

    const result = buildResult(output, tools)
    state.stepResults[step.id] = result
    state.stepStatus[step.id] = 'complete'
    state.stepEndTimes[step.id] = Date.now()

    addEvent('casey', 'step_complete', `${step.label} -- done`)
    flowNotify(patientId)
    persistFlowState(patientId)

    // Process queued user messages between steps
    while (state.userMessageQueue.length > 0) {
      const queued = state.userMessageQueue.shift()!
      try {
        let queuedOutput = ''

        const stream = backendAdapter.streamCasePanelChat({
          workspaceId,
          agentId: 'casey',
          message: queued.text,
          mode: 'flow',
          sessionId: state.sessionId,
          signal,
        })

        for await (const event of stream) {
          const sseEvent = event as CasePanelSSEEvent
          const eventType = getEventType(sseEvent)
          const data = sseEvent.data || {}
          if (eventType === 'message' || eventType === 'token') {
            if (data.content) queuedOutput += data.content
          } else if (eventType === 'complete' && data.content) {
            queuedOutput = data.content
          }
        }

        queued.resolve(buildResult(queuedOutput))
      } catch (err) {
        console.error('[PipelineManager] Failed to process queued user message:', err)
        queued.resolve(null)
      }
    }
  }

  state.status = 'complete'
  // Clean up abort controller on natural completion
  abortControllers.delete(`flow-${patientId}`)
  addEvent('casey', 'pipeline_complete', 'IPASS signout complete')
  flowNotify(patientId)
  persistFlowState(patientId)
}
